import { useReplaceActie } from "@/entities/actie";
import { VsmActie } from "@/entities/actie/domain";
import { Maatregel, useGetDossierVsmById, VsmVergelijking } from "@/entities/vsm";
import {
    EditableVsm,
    OudeVsm,
    useIsEditingMaatregel,
    useVsmEditorContext,
    useVsmEditorMaatregelen,
    VsmEditorProvider,
    VsmVergelijkingProvider,
} from "@/features/vsm";
import { Button, DialogFooter, ErrorDisplay, Icon } from "@/shared/components";
import { cn } from "@/shared/utils";
import { ReactNode, useMemo, useState } from "react";
import { ActieDialog, DeclineActieConfirmation } from "../actie-dialog";
import { VsmActieDialogProvider } from "./vsm-actie-dialog-provider.tsx";

type VsmActieDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    dossierId: string;
    actie: VsmActie;
};

export function VsmActieDialog({ open, onOpenChange, dossierId, actie }: Readonly<VsmActieDialogProps>) {
    const { data: vsm, isPending: isVsmPending } = useGetDossierVsmById(dossierId);

    // the toelichting of a maatregel in the huidige vsm is offered as a suggestion on the matching nieuwe maatregel
    const initialMaatregelen = useMemo(
        () => Maatregel.withHuidigeToelichting(actie.nieuweWaarde.maatregelen, vsm?.maatregelen ?? []),
        [actie.nieuweWaarde.maatregelen, vsm?.maatregelen]
    );

    const includeInRijdtGroep = useMemo(() => (vsm ? VsmVergelijking.belongsInRijdtGroep(vsm.maatregelen) : undefined), [vsm]);

    // return null while pending, so we don't initialize with stale data
    if (isVsmPending) {
        return null;
    }

    // if we have an existing vsm, we should make the dialog a bit wider
    const minWidth = !vsm ? "min-w-[736px]" : "min-w-[1216px]";

    return (
        <VsmActieDialogProvider currentVsm={vsm}>
            <VsmEditorProvider
                initialMaatregelen={initialMaatregelen}
                initialStatus={actie.nieuweWaarde.status}
                includeInRijdtGroep={includeInRijdtGroep}
            >
                <VsmVergelijkingProvider huidigeVsm={vsm}>
                    <VsmActieDialogContainer
                        className={minWidth}
                        dossierId={dossierId}
                        actie={actie}
                        open={open}
                        onOpenChange={onOpenChange}
                    >
                        <div className="flex flex-row gap-2">
                            {vsm && <VsmDialogContent versie="Huidig" />}
                            {vsm && <Icon name="arrow-right" className="text-content-emphasis mt-15" />}
                            <VsmDialogContent versie="Nieuw" />
                        </div>
                    </VsmActieDialogContainer>
                </VsmVergelijkingProvider>
            </VsmEditorProvider>
        </VsmActieDialogProvider>
    );
}

type VsmActieDialogContainerProps = VsmActieDialogProps & {
    className?: string;
    children: ReactNode;
};

function VsmActieDialogContainer({ className, open, onOpenChange, dossierId, actie, children }: VsmActieDialogContainerProps) {
    const { reset: resetMaatregelen, resetToelichting } = useVsmEditorContext();

    return (
        <ActieDialog
            open={open}
            onOpenChange={(open) => {
                if (!open) {
                    resetMaatregelen();
                    resetToelichting();
                }
                onOpenChange(open);
            }}
            dossierId={dossierId}
            actie={actie}
            footer={VsmDialogFooter}
            className={cn(className, "min-h-0 flex-1 overflow-y-auto")}
        >
            {children}
        </ActieDialog>
    );
}

type VsmDialogContentProps = {
    versie: "Huidig" | "Nieuw";
};
function VsmDialogContent({ versie }: Readonly<VsmDialogContentProps>) {
    return (
        <div
            data-variant={versie === "Nieuw" ? "nieuw" : "oud"}
            className="group data-[variant=nieuw]:text-content-emphasis text-content-subtle flex w-full flex-col gap-4"
        >
            <h4 className="text-l font-medium">{versie}</h4>
            {versie === "Nieuw" && <EditableVsm />}
            {versie === "Huidig" && <OudeVsm />}
        </div>
    );
}

type VsmDialogFooterProps = {
    dossierId: string;
    actie: VsmActie;
    onClose: () => void;
    className?: string;
};

function VsmDialogFooter({ className, dossierId, actie, onClose }: Readonly<VsmDialogFooterProps>) {
    const maatregelen = useVsmEditorMaatregelen();
    const isEditingVersie = useIsEditingMaatregel();
    const { mutate: replaceActie, error, isPending } = useReplaceActie();
    const [weigerenPopupOpen, setWeigerenPopupOpen] = useState(false);
    const { status, toelichting } = useVsmEditorContext();

    const hasMaatregelen = maatregelen.some((m) => m.type !== "RijdtGroep");

    const handleAcceptAction = () => {
        if (!hasMaatregelen) return;

        replaceActie(
            {
                dossierId,
                actieId: actie.id,
                actie: VsmActie.toDto(VsmActie.update(actie, maatregelen, toelichting, status)),
            },
            { onSuccess: () => onClose() }
        );
    };

    return (
        <DialogFooter className={cn("flex flex-col justify-between", className)}>
            <div className="flex w-full items-center justify-end gap-2">
                {!hasMaatregelen && (
                    <p className={"text-destructive text-s mb-1 flex items-center gap-2"} data-testid="error-missende-maatregelen">
                        <Icon name="error-filled" size="xs" />
                        Voeg minstens 1 maatregel toe
                    </p>
                )}
                <Button
                    data-testid="decline-actie"
                    variant="tertiary"
                    destructive
                    onClick={() => setWeigerenPopupOpen(true)}
                    disabled={isPending || isEditingVersie}
                >
                    Weigeren
                </Button>
                <Button
                    data-testid="confirm-actie"
                    variant="primary"
                    onClick={handleAcceptAction}
                    disabled={isPending || isEditingVersie || !hasMaatregelen}
                >
                    Bevestigen
                </Button>
            </div>
            {error && <ErrorDisplay error={error} />}
            <DeclineActieConfirmation
                actie={actie}
                isOpen={weigerenPopupOpen}
                setIsOpen={setWeigerenPopupOpen}
                closeActieDialog={onClose}
                dossierId={dossierId}
            />
        </DialogFooter>
    );
}
